/**
 * axios instance + interceptor.
 * JWT Bearer 자동 부착 / 401 refresh + 동시 401 race 큐 / 응답은 response.data 만 반환.
 */
import axios from 'axios';
import router from '@/router';
import { useAuthStore } from '@/stores/auth';
import { useLocaleStore } from '@/stores/locale';
import { REQUEST_ID_HEADER, newRequestId } from '@/services/requestId';

// 세션 정리 + 로그인 화면 이동 (refresh 불가/실패 공통). 현재 라우트를 redirect 쿼리로 보존.
function clearAndRedirectToLogin(auth) {
  auth.clear();
  const current = router.currentRoute.value;
  if (current.name !== 'Login') {
    // push 아닌 replace - 만료된 보호 URL 을 history 에 남기지 않음 (뒤로가기 바운스 방지)
    router.replace({ name: 'Login', query: { redirect: current.fullPath } });
  }
}

const httpConfig = {
  baseURL: import.meta.env.VITE_API_BASE_URL || '', // 비어 있으면 vite proxy 사용
  timeout: 30000,
  headers: { 'Content-Type': 'application/json; charset=UTF-8' },
};
const http = axios.create(httpConfig);

// Request interceptor
http.interceptors.request.use((config) => {
  const auth = useAuthStore();
  if (auth.accessToken) {
    config.headers.Authorization = `${auth.tokenType || 'Bearer'} ${auth.accessToken}`;
  }
  // 요청 추적 ID. 서버가 MDC 에 올려 로그에 찍고 같은 값을 응답 헤더로 돌려준다.
  // 문의받은 오류 화면의 ID 로 서버 로그를 특정하는 용도.
  config.headers[REQUEST_ID_HEADER] = newRequestId();
  // 화면 언어는 세션이 아니라 요청의 속성이다. 서버 메시지·코드명이 같은 값으로 결정된다.
  config.headers['Accept-Language'] = useLocaleStore().locale;
  // 작업 대상 법인. 소속 법인과 같으면 서버가 검증 없이 통과시키고, 다르면 전체 법인 접근 권한을 본다.
  if (auth.activeCompanyCd) config.headers['X-Insait-Company-Cd'] = auth.activeCompanyCd;
  return config;
});

// Response interceptor + 401 refresh 큐
let refreshing = false;
let waitQueue = []; // 동시 401 들이 새 token 을 기다리며 적재되는 곳

function flushQueue(error, token) {
  waitQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve(token);
  });
  waitQueue = [];
}

http.interceptors.response.use(
  // interceptor 는 response.data 만 반환(래핑 없음). 오류 형식 { error: { code, message } } 는 각 호출부(apiErr)가 해석.
  (response) => response.data,
  async (error) => {
    const original = error.config;
    const status = error.response?.status;
    const isAuthExpired = status === 401;

    // 선택 법인이 더 이상 유효하지 않다(법인 폐지·권한 회수·같은 기기의 이전 계정 잔존값).
    // 소속 법인으로 되돌려 1회 재시도한다. 이 복구가 없으면 /api/auth/me 까지 403 이라
    // 클라이언트가 법인 목록을 갱신할 길이 없어 영구히 잠긴다.
    if (status === 403 && error.response?.data?.error?.code === 'SECURITY__COMPANY_FORBIDDEN'
      && original && !original._companyRetry) {
      const auth = useAuthStore();
      if (auth.currentCompanyCd && auth.currentCompanyCd !== auth.companyCd) {
        auth.resetCompany();
        original._companyRetry = true;
        delete original.headers['X-Insait-Company-Cd'];   // 요청 인터셉터가 소속 법인으로 다시 채운다
        return http(original);
      }
    }

    // 메뉴 권한이 바뀌어 이 화면 API 가 막혔다. LNB 를 서버 기준으로 다시 맞춘다(D-199).
    // menu store 가 http 를 쓰므로 순환 참조를 피해 지연 import
    if (status === 403 && error.response?.data?.error?.code === 'SECURITY__MENU_FORBIDDEN') {
      import('@/stores/menu').then((m) => m.useMenuStore().refresh({ minGapMs: 5000 })).catch(() => {});
    }

    // 인증 만료가 아니면 그대로 전파
    if (!isAuthExpired) {
      return Promise.reject(error);
    }

    // 공개(비로그인) 화면 호출 - 401 이어도 refresh/로그인 리다이렉트를 타지 않는다
    // (redirect 쿼리에 공개 토큰이 유출되는 것 방지. 호출부가 { skipAuthRedirect: true } 지정)
    if (original?.skipAuthRedirect) {
      return Promise.reject(error);
    }

    const auth = useAuthStore();

    // 복구 불가 (재시도할 config 없음 / 이미 한 번 재시도함 / refresh token 없음)
    // -> 세션 정리 + 로그인 화면 이동.
    if (!original || original._retry || !auth.refreshToken) {
      clearAndRedirectToLogin(auth);
      return Promise.reject(error);
    }

    if (refreshing) {
      // refresh 진행 중 - 큐에 적재
      return new Promise((resolve, reject) => {
        waitQueue.push({
          resolve: (token) => {
            original.headers.Authorization = `${auth.tokenType} ${token}`;
            original._retry = true;
            resolve(http(original));
          },
          reject,
        });
      });
    }

    refreshing = true;
    original._retry = true;
    try {
      const refreshResp = await axios.post(
        `${http.defaults.baseURL || ''}/api/auth/refresh`,
        { refreshToken: auth.refreshToken },
        { headers: { 'Content-Type': 'application/json' } },
      );
      const newToken = refreshResp.data?.data?.accessToken;
      if (!newToken) throw new Error('refresh returned no accessToken');
      auth.setAccessToken(newToken);
      flushQueue(null, newToken);
      original.headers.Authorization = `${auth.tokenType} ${newToken}`;
      return http(original);
    } catch (refreshErr) {
      flushQueue(refreshErr, null);
      // refresh 실패 = 세션 복구 불가 -> 즉시 로그인 화면 이동 (다음 라우트 이동 대기 X)
      clearAndRedirectToLogin(auth);
      return Promise.reject(refreshErr);
    } finally {
      refreshing = false;
    }
  },
);

export default http;
